import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Briefcase, Gavel, Receipt, FileText } from "lucide-react";
import { StatusBadge, EmptyState } from "@/components/Ui";
import { fmtDate, fmtDateTime } from "@/lib/time";

// Statuses that mean the litigation is no longer active.
// A client portal user must not see these counted as "open".
const TERMINAL_CASE_STATUSES = new Set([
    "closed", "disposed", "withdrawn", "dismissed", "archived", "settled"
]);

// A hearing is "upcoming" only when it's in the future AND not already completed.
const COMPLETED_HEARING_STATUSES = new Set([
    "completed", "heard", "disposed", "done"
]);

function Tile({ icon: Icon, label, value, hint, to, testId }) {
    const inner = (
        <div className="bg-muted border border-border p-6 h-full hover:border-primary transition-colors"
             data-testid={testId}>
            <div className="flex items-start justify-between mb-3">
                <div className="overline">{label}</div>
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.5}/>
            </div>
            <div className="font-serif text-4xl font-light">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}
        </div>
    );
    return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function PortalOverview() {
    const [data, setData] = useState({ me: null, cases: [], hearings: [], invoices: [], documents: [] });

    useEffect(() => {
        Promise.all([
            api.get("/portal/me"),
            api.get("/portal/cases"),
            api.get("/portal/hearings"),
            api.get("/portal/invoices"),
            api.get("/portal/documents"),
        ])
            .then(([me, cs, hs, inv, docs]) => setData({
                me: me.data, cases: cs.data, hearings: hs.data, invoices: inv.data, documents: docs.data,
            }))
            .catch(() => {});
    }, []);

    // --- Derived counters: respect the visibility rules the user asked for ---
    const openCases = data.cases.filter(
        c => !TERMINAL_CASE_STATUSES.has((c.status || "").toLowerCase())
    );
    const upcomingHearings = data.hearings
        .filter(h => !COMPLETED_HEARING_STATUSES.has((h.status || "").toLowerCase()))
        .filter(h => !h.scheduled_at || new Date(h.scheduled_at) >= new Date(new Date().setHours(0, 0, 0, 0)))
        .sort((a, b) => new Date(a.scheduled_at || 0) - new Date(b.scheduled_at || 0));   // A-Z by date
    const outstandingInvoices = data.invoices.filter(i => (i.status || "").toLowerCase() !== "paid");
    const outstandingAmount = outstandingInvoices.reduce(
        (s, i) => s + (Number(i.amount || 0) - Number(i.paid_amount || 0)), 0
    );
    const documentsCount = data.documents.length;

    // Recent cases — newest first (Z-A by created_at), exclude terminal
    const recentCases = [...openCases]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    return (
        <div className="space-y-10">
            <div>
                <div className="overline mb-3">Your matters</div>
                <h1 className="font-serif text-4xl sm:text-5xl font-light">
                    Hello, <span className="italic">{data.me?.client?.name?.split(" ")[0] || "there"}.</span>
                </h1>
                <p className="text-muted-foreground text-sm mt-3">Everything {data.me?.firm?.name || "your advocate"} has on the table for you.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Tile testId="portal-card-open-cases"
                       icon={Briefcase} label="Open cases" value={openCases.length}
                       to="/portal/cases"/>
                <Tile testId="portal-card-upcoming-hearings"
                       icon={Gavel} label="Upcoming hearings" value={upcomingHearings.length}
                       hint={upcomingHearings[0] ? fmtDate(upcomingHearings[0].scheduled_at) : ""}
                       to="/portal/hearings"/>
                <Tile testId="portal-card-outstanding"
                       icon={Receipt} label="Outstanding"
                       value={`₹${outstandingAmount.toLocaleString()}`}
                       hint={outstandingInvoices.length
                           ? `${outstandingInvoices.length} unpaid ${outstandingInvoices.length === 1 ? "invoice" : "invoices"}`
                           : ""}
                       to="/portal/invoices"/>
                <Tile testId="portal-card-documents"
                       icon={FileText} label="Documents"
                       value={documentsCount > 0 ? documentsCount : "—"}
                       hint={documentsCount > 0 ? "shared across your matters" : ""}
                       to="/portal/documents"/>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-muted border border-border p-6">
                    <div className="overline mb-4">Recent cases</div>
                    {recentCases.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No litigations yet.</div>
                    ) : (
                        <div className="divide-y divide-border">
                            {recentCases.slice(0, 5).map(c => (
                                <Link to={`/portal/cases/${c.id}`} key={c.id} className="block py-3 hover:bg-background -mx-2 px-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm truncate">{c.title}</div>
                                        <StatusBadge status={c.status}/>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 font-mono">{c.internal_number} · {c.stage}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
                <div className="bg-muted border border-border p-6">
                    <div className="overline mb-4">Next hearings</div>
                    {upcomingHearings.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">No hearings scheduled.</div>
                    ) : (
                        <div className="divide-y divide-border">
                            {upcomingHearings.slice(0, 5).map(h => (
                                <div key={h.id} className="py-3">
                                    <div className="font-mono text-xs text-primary">{fmtDateTime(h.scheduled_at)}</div>
                                    <div className="text-sm mt-1">{h.purpose || "Hearing"}</div>
                                    <div className="text-xs text-muted-foreground">{h.court || "—"}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
